'use client';

import Loader from '@pickleballinc/features/src/Loader/Loader';
import { Button } from '@pickleballinc/ui/components/ui/button';
import { Tooltip, TooltipContent, TooltipPortal, TooltipProvider, TooltipTrigger } from '@pickleballinc/ui/components/ui/tooltip';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { LineupPlayerSelect } from '@/components/atoms/scoreCards/LineupPlayerSelect';
import { PlayerType } from '@/components/constants/enums';
import { useScoreCard } from '@/contexts/ScoreCardProvider';
import { LineupPlayerOption, Match } from '@/entities';
import { Player as LineupPlayer } from '@/entities/matchup/Lineups';
import { LineupEntryOrder } from '@/entities/season-division/SeasonDivisionPool';
import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { matchKeys } from '@/hooks/matches/matchKeys';
import { useChangePlayerMutation } from '@/hooks/matches/useChangePlayer';
import { DateUtil } from '@/utils/date';
import {
	calculateDoublesRating,
	filterPlayersByGenderRule,
	lineupPlayerToLineupPlayerOption,
	matchSubPlayerToLineupPlayerOption
} from '@/utils/helpers';

interface LineupCardProps {
	match: Match;
	previousPodMatch?: Match;
	awayTeamPlayers: LineupPlayer[];
	homeTeamPlayers: LineupPlayer[];
	homeTeamTitle: string;
	awayTeamTitle: string;
	className?: string;
	showOpponentLineup?: boolean;
	teamCaptain?: 'home' | 'away' | undefined;
	lineupEntryOrder?: LineupEntryOrder;
	canCaptainSetLineup?: boolean;
}

export const LineupCard = ({
	match,
	awayTeamPlayers,
	homeTeamPlayers,
	homeTeamTitle,
	awayTeamTitle,
	className = '',
	showOpponentLineup = false,
	teamCaptain = undefined,
	lineupEntryOrder,
	canCaptainSetLineup = false,
	previousPodMatch
}: LineupCardProps) => {
	const teamLeagueInfo = useTeamLeagueInfo();
	const queryClient = useQueryClient();

	const { updateMatchLineupStatus, updateSelectedPlayers, clearSelectedPlayers, matchupData, getSelectedPlayers, haveSelectedPlayersChanged } =
		useScoreCard();
	const homeTeamEnterLineupFirst = lineupEntryOrder === LineupEntryOrder.HOME_TEAM_FIRST_LINEUP_ENTRY;
	const awayTeamEnterLineupFirst = lineupEntryOrder === LineupEntryOrder.AWAY_TEAM_FIRST_LINEUP_ENTRY;
	const hasSelectedPlayersChanged = haveSelectedPlayersChanged(match.matchUuid);

	const { ianaTzIdentifier, abbreviation } = match.timezone;

	const formattedStartDate = match.matchPlannedStart
		? DateUtil.formatUTCInTimezone(match.matchPlannedStart, 'MMM D YYYY h:mm A', ianaTzIdentifier)
		: '';

	const subPlayerGenderFilter = (p: LineupPlayerOption) => {
		const group = match.playerGroupTitle?.toLowerCase() ?? '';
		if (group === 'mens') return p.gender?.toLowerCase() === 'm';
		if (group === 'womens') return p.gender?.toLowerCase() === 'f';
		return true;
	};

	const formattedHomePlayers: LineupPlayerOption[] = filterPlayersByGenderRule(homeTeamPlayers, match.playerGroupTitle)
		.map(lineupPlayerToLineupPlayerOption)
		.concat((match.teamOneSubPlayers ?? []).map(matchSubPlayerToLineupPlayerOption).filter(subPlayerGenderFilter));

	const formattedAwayPlayers: LineupPlayerOption[] = filterPlayersByGenderRule(awayTeamPlayers, match.playerGroupTitle)
		.map(lineupPlayerToLineupPlayerOption)
		.concat((match.teamTwoSubPlayers ?? []).map(matchSubPlayerToLineupPlayerOption).filter(subPlayerGenderFilter));

	const isDoubles = match.formatTitle?.toLowerCase().includes('doubles') || false;
	const isMixed = match.playerGroupTitle?.toLowerCase().includes('mixed') || false;
	const isMixedDoubles = isDoubles && isMixed;

	const homeFemalePlayers = formattedHomePlayers.filter((p) => (p.gender || '').toLowerCase() === 'f');
	const homeMalePlayers = formattedHomePlayers.filter((p) => (p.gender || '').toLowerCase() === 'm');
	const awayFemalePlayers = formattedAwayPlayers.filter((p) => (p.gender || '').toLowerCase() === 'f');
	const awayMalePlayers = formattedAwayPlayers.filter((p) => (p.gender || '').toLowerCase() === 'm');

	// Get currently selected players to prevent duplicate selection
	const currentSelection = getSelectedPlayers(match.matchUuid);
	const selectedHomePlayerOne = currentSelection.teamOnePlayerOneUuid || match.teamOnePlayerOneUuid || '';
	const selectedHomePlayerTwo = currentSelection.teamOnePlayerTwoUuid || match.teamOnePlayerTwoUuid || '';
	const selectedAwayPlayerOne = currentSelection.teamTwoPlayerOneUuid || match.teamTwoPlayerOneUuid || '';
	const selectedAwayPlayerTwo = currentSelection.teamTwoPlayerTwoUuid || match.teamTwoPlayerTwoUuid || '';

	// When match data (from refetch or RTE) confirms our locally-saved selections, clear them
	// so that subsequent RTE updates from other browsers can flow through unobstructed.
	useEffect(() => {
		const hasAnyLocalSelection =
			currentSelection.teamOnePlayerOneUuid ||
			currentSelection.teamOnePlayerTwoUuid ||
			currentSelection.teamTwoPlayerOneUuid ||
			currentSelection.teamTwoPlayerTwoUuid;

		if (!hasAnyLocalSelection) return;

		const allConfirmed =
			(!currentSelection.teamOnePlayerOneUuid || currentSelection.teamOnePlayerOneUuid === match.teamOnePlayerOneUuid) &&
			(!currentSelection.teamOnePlayerTwoUuid || currentSelection.teamOnePlayerTwoUuid === match.teamOnePlayerTwoUuid) &&
			(!currentSelection.teamTwoPlayerOneUuid || currentSelection.teamTwoPlayerOneUuid === match.teamTwoPlayerOneUuid) &&
			(!currentSelection.teamTwoPlayerTwoUuid || currentSelection.teamTwoPlayerTwoUuid === match.teamTwoPlayerTwoUuid);

		if (allConfirmed) {
			clearSelectedPlayers(match.matchUuid);
		}
	}, [match.teamOnePlayerOneUuid, match.teamOnePlayerTwoUuid, match.teamTwoPlayerOneUuid, match.teamTwoPlayerTwoUuid]); // eslint-disable-line react-hooks/exhaustive-deps

	// Base player pools for each position (with gender rules for mixed doubles)
	const awayPlayerOnePool = isMixedDoubles ? awayFemalePlayers : formattedAwayPlayers;
	const awayPlayerTwoPool = isMixedDoubles ? awayMalePlayers : formattedAwayPlayers;
	const homePlayerOnePool = isMixedDoubles ? homeFemalePlayers : formattedHomePlayers;
	const homePlayerTwoPool = isMixedDoubles ? homeMalePlayers : formattedHomePlayers;

	// Final dropdown options (exclude the other selected player from same team)
	const awayPlayerOneOptions = awayPlayerOnePool.filter((p) => p.userUuid !== selectedAwayPlayerTwo);
	const awayPlayerTwoOptions = awayPlayerTwoPool.filter((p) => p.userUuid !== selectedAwayPlayerOne);
	const homePlayerOneOptions = homePlayerOnePool.filter((p) => p.userUuid !== selectedHomePlayerTwo);
	const homePlayerTwoOptions = homePlayerTwoPool.filter((p) => p.userUuid !== selectedHomePlayerOne);

	const { mutate: changePlayerMutation, isPending: isChangingPlayer } = useChangePlayerMutation({
		onSuccessCallback: () => {
			queryClient.invalidateQueries({ queryKey: matchKeys.matches(matchupData.uuid) });
			updateMatchLineupStatus(match.matchUuid, true);
		}
	});

	const handlePlayerSelect = (selectedPlayerId: string | number, playerType: PlayerType, side: 'home' | 'away') => {
		const isHome = side === 'home';
		const playerTypeMap = {
			[PlayerType.PLAYER_ONE]: isHome ? 'teamOnePlayerOneUuid' : 'teamTwoPlayerOneUuid',
			[PlayerType.PLAYER_TWO]: isHome ? 'teamOnePlayerTwoUuid' : 'teamTwoPlayerTwoUuid'
		};

		updateSelectedPlayers(match.matchUuid, playerTypeMap[playerType], selectedPlayerId.toString());
	};

	const handleOnSaveLineup = () => {
		const selectedPlayers = getSelectedPlayers(match.matchUuid);

		const validPlayers = {
			...(selectedPlayers.teamOnePlayerOneUuid && { swappedOutButPlayedTeam1UserId1: selectedPlayers.teamOnePlayerOneUuid }),
			...(selectedPlayers.teamOnePlayerTwoUuid && { swappedOutButPlayedTeam1UserId2: selectedPlayers.teamOnePlayerTwoUuid }),
			...(selectedPlayers.teamTwoPlayerOneUuid && { swappedOutButPlayedTeam2UserId1: selectedPlayers.teamTwoPlayerOneUuid }),
			...(selectedPlayers.teamTwoPlayerTwoUuid && { swappedOutButPlayedTeam2UserId2: selectedPlayers.teamTwoPlayerTwoUuid })
		};

		changePlayerMutation({
			matchId: match.matchUuid,
			updateData: validPlayers
		});
	};

	const getPlayerByUuid = (players: LineupPlayerOption[], playerUuid: string | null): LineupPlayerOption | undefined =>
		players.find((player) => player.userUuid === playerUuid);

	const checkHomeTeamLineup = teamCaptain === 'home' && awayTeamEnterLineupFirst && !match.teamTwoPlayerOneUuid && !match.teamTwoPlayerTwoUuid;
	const checkAwayTeamLineup = teamCaptain === 'away' && homeTeamEnterLineupFirst && !match.teamOnePlayerOneUuid && !match.teamOnePlayerTwoUuid;

	const shouldShowDoublesRatingWarning = !!teamLeagueInfo.usePodValidation && !!previousPodMatch && isDoubles && !isMixed;
	const firstPodMatchDoublesRating = calculateDoublesRating({
		match: previousPodMatch,
		homePlayers: homeTeamPlayers,
		awayPlayers: awayTeamPlayers,
		isEnabled: shouldShowDoublesRatingWarning
	});

	const secondPodMatchDoublesRating = calculateDoublesRating({
		match,
		homePlayers: homeTeamPlayers,
		awayPlayers: awayTeamPlayers,
		currentSelectedPlayers: currentSelection,
		isEnabled: shouldShowDoublesRatingWarning
	});

	const showHomeTeamDoublesRatingWarning =
		shouldShowDoublesRatingWarning && secondPodMatchDoublesRating.homePlayerRating > firstPodMatchDoublesRating.homePlayerRating;
	const showAwayTeamDoublesRatingWarning =
		shouldShowDoublesRatingWarning && secondPodMatchDoublesRating.awayPlayerRating > firstPodMatchDoublesRating.awayPlayerRating;

	const toolTipContent = (() => {
		let message = null;
		if (checkHomeTeamLineup) {
			message = ` Waiting for away team to select their lineups.`;
		}

		if (checkAwayTeamLineup) {
			message = ` Waiting for home team to select their lineups.`;
		}

		return message;
	})();

	return (
		<div className={`relative bg-white border border-[#EF6820] rounded-lg p-4 w-full lg:max-w-[600px] ${className}`}>
			{isChangingPlayer ? (
				<Loader backgroundColor="bg-[#EF6820]" containerClassName="w-full h-full absolute rounded-md z-10 opacity-90" isComponent />
			) : null}
			<div className="flex justify-between mb-3">
				<h3 className="text-lg font-medium text-gray-900 mb-2 sm:mb-0">{match.eventTitle || 'Match'}</h3>
				{canCaptainSetLineup ? (
					<TooltipProvider delayDuration={0} skipDelayDuration={0}>
						<Tooltip key={toolTipContent}>
							<TooltipTrigger asChild>
								<div className="inline-block">
									<Button
										onClick={handleOnSaveLineup}
										variant="outline"
										className="text-sm text-gray-800 w-fit"
										disabled={isChangingPlayer || !hasSelectedPlayersChanged || checkHomeTeamLineup || checkAwayTeamLineup}
									>
										Save lineup
									</Button>
								</div>
							</TooltipTrigger>
							<TooltipPortal>
								{toolTipContent ? (
									<TooltipContent className="bg-gray-900">
										<p>{toolTipContent}</p>
									</TooltipContent>
								) : null}
							</TooltipPortal>
						</Tooltip>
					</TooltipProvider>
				) : null}
			</div>

			<div className="text-sm text-gray-500 mb-4">
				{formattedStartDate ? (
					<>
						<span>
							{formattedStartDate} {abbreviation}
						</span>
						{match.courtTitle ? <span className="text-gray-400 mx-1">|</span> : null}
					</>
				) : null}
				{match.courtTitle ? <span>Court: {match.courtTitle}</span> : null}
				<div> {match.scoreFormatShorthand}</div>
			</div>

			<div className="mb-6">
				<h4 className="text-lg font-bold text-gray-900 text-center mb-4">{awayTeamTitle} (A)</h4>
				<div className="flex flex-col gap-4 sm:flex-row sm:gap-4 xl:gap-8">
					<LineupPlayerSelect
						players={awayPlayerOneOptions}
						onSelect={(selectedPlayerId) => handlePlayerSelect(selectedPlayerId.toString(), PlayerType.PLAYER_ONE, 'away')}
						playerLabel="Player 1"
						placeholder="Select Player 1"
						selectedPlayer={getPlayerByUuid(awayPlayerOneOptions, selectedAwayPlayerOne)}
						isLocked={!canCaptainSetLineup || teamCaptain === 'home'}
						isBlurred={!showOpponentLineup}
					/>
					{isDoubles ? (
						<LineupPlayerSelect
							players={awayPlayerTwoOptions}
							onSelect={(selectedPlayerId) => handlePlayerSelect(selectedPlayerId.toString(), PlayerType.PLAYER_TWO, 'away')}
							playerLabel="Player 2"
							placeholder="Select Player 2"
							selectedPlayer={getPlayerByUuid(awayPlayerTwoOptions, selectedAwayPlayerTwo)}
							isLocked={!canCaptainSetLineup || teamCaptain === 'home'}
							isBlurred={!showOpponentLineup}
						/>
					) : null}
				</div>
				{showAwayTeamDoublesRatingWarning ? (
					<div className="mt-4 text-xs text-center text-darkorange-600">Combined partner rating exceeds Pod 1</div>
				) : null}
			</div>

			<div className="flex items-center justify-center mb-6">
				<div className="flex-1 h-px bg-gray-300" />
				<span className="px-4 text-gray-500 font-medium">vs</span>
				<div className="flex-1 h-px bg-gray-300" />
			</div>

			<div>
				<div>
					<h4 className="text-lg font-bold text-gray-900 text-center mb-4">{homeTeamTitle} (H)</h4>
					<div className="flex flex-col gap-4 sm:flex-row sm:gap-4 xl:gap-8">
						<LineupPlayerSelect
							players={homePlayerOneOptions}
							onSelect={(selectedPlayerId) => handlePlayerSelect(selectedPlayerId.toString(), PlayerType.PLAYER_ONE, 'home')}
							playerLabel="Player 1"
							placeholder="Select Player 1"
							selectedPlayer={getPlayerByUuid(homePlayerOneOptions, selectedHomePlayerOne)}
							isLocked={!canCaptainSetLineup || teamCaptain === 'away'}
							isBlurred={!showOpponentLineup}
						/>
						{isDoubles ? (
							<LineupPlayerSelect
								players={homePlayerTwoOptions}
								onSelect={(selectedPlayerId) => handlePlayerSelect(selectedPlayerId.toString(), PlayerType.PLAYER_TWO, 'home')}
								playerLabel="Player 2"
								placeholder="Select Player 2"
								selectedPlayer={getPlayerByUuid(homePlayerTwoOptions, selectedHomePlayerTwo)}
								isLocked={!canCaptainSetLineup || teamCaptain === 'away'}
								isBlurred={!showOpponentLineup}
							/>
						) : null}
					</div>
					{showHomeTeamDoublesRatingWarning ? (
						<div className="mt-4 text-xs text-center text-darkorange-600">Combined partner rating exceeds Pod 1</div>
					) : null}
				</div>
			</div>
		</div>
	);
};
